/******/ (() => { // webpackBootstrap

/* ===== Call Button Component ===== */
/* Handles mobile tel: links and desktop popup for call functionality */

class CallButton extends HTMLElement {
  constructor() {
    super();

    /* ===== Bind Event Handlers ===== */
    this.showPopup = this.showPopup.bind(this);
    this.hidePopup = this.hidePopup.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);

    /* ===== Cache DOM Elements ===== */
    this.cacheDOMElements();
  }

  cacheDOMElements() {
    /* ===== Cache the DOM elements ===== */
    this.desktopButton = this.querySelector('[data-call-button-desktop]');
    this.mobileButton = this.querySelector('[data-call-button-mobile]');
    this.popupTemplate = this.querySelector('[data-call-popup-template]');
    this.sectionId = this.getAttribute('data-section-id');
    this.phoneNumber = this.getAttribute('data-phone-number');
    this.phoneNumberTel = this.getAttribute('data-phone-number-tel');
    this.popupTitle = this.getAttribute('data-popup-title');
    this.popupText = this.getAttribute('data-popup-text');

    /* ===== Popup Elements (populated when popup is shown) ===== */
    this.popup = null;
    this.overlay = null;
    this.closeButton = null;
  }

  connectedCallback() {
    /* ===== Register Component ===== */
    window.wetheme.webcomponentRegistry.register({ key: 'call-button' });

    /* ===== Attach Event Listeners ===== */
    if (this.desktopButton) {
      this.desktopButton.addEventListener('click', this.showPopup);
    }
  }

  disconnectedCallback() {
    /* ===== Remove Event Listeners ===== */
    if (this.desktopButton) {
      this.desktopButton.removeEventListener('click', this.showPopup);
    }

    /* ===== Clean Up Popup ===== */
    this.removePopupFromDOM();
  }

  showPopup(e) {
    /* ===== Show the popup on desktop ===== */
    e.preventDefault();
    e.stopPropagation();

    /* ===== Clone Template and Insert Popup ===== */
    if (!this.popupTemplate) return;

    const popupContent = this.popupTemplate.content.cloneNode(true);
    document.body.appendChild(popupContent);

    /* ===== Cache Popup Elements ===== */
    this.overlay = document.querySelector('[data-call-popup-overlay]');
    this.popup = document.querySelector('[data-call-popup]');
    this.closeButton = this.popup?.querySelector('[data-call-popup-close]');

    /* ===== Store Previously Focused Element ===== */
    this.previousActiveElement = document.activeElement;

    /* ===== Attach Popup Event Listeners ===== */
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.hidePopup);
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }
    document.addEventListener('keydown', this.handleKeydown);

    /* ===== Trigger Animation ===== */
    requestAnimationFrame(() => {
      this.overlay?.classList.add('is-visible');
      this.popup?.classList.add('is-visible');

      /* ===== Focus Management ===== */
      this.closeButton?.focus();
    });

    /* ===== Emit Event ===== */
    if (window.eventBus) {
      window.eventBus.emit('call:popup:open', { sectionId: this.sectionId });
    }
  }

  hidePopup() {
    /* ===== Hide the popup ===== */
    if (!this.popup || !this.overlay) return;

    /* ===== Remove Visible Classes ===== */
    this.overlay.classList.remove('is-visible');
    this.popup.classList.remove('is-visible');

    /* ===== Clean Up After Transition ===== */
    this.popup.addEventListener('transitionend', () => {
      this.removePopupFromDOM();
    }, { once: true });

    /* ===== Fallback Cleanup ===== */
    setTimeout(() => {
      this.removePopupFromDOM();
    }, 400);

    /* ===== Restore Focus ===== */
    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      this.previousActiveElement.focus();
    }

    /* ===== Emit Event ===== */
    if (window.eventBus) {
      window.eventBus.emit('call:popup:close', { sectionId: this.sectionId });
    }
  }

  removePopupFromDOM() {
    /* ===== Remove popup elements from DOM ===== */
    /* ===== Remove Event Listeners ===== */
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.hidePopup);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }
    document.removeEventListener('keydown', this.handleKeydown);

    /* ===== Remove Elements from DOM ===== */
    this.overlay?.remove();
    this.popup?.remove();

    /* ===== Clear References ===== */
    this.overlay = null;
    this.popup = null;
    this.closeButton = null;
  }

  handleOverlayClick(e) {
    /* ===== Handle click on overlay to close popup ===== */
    if (e.target === this.overlay) {
      this.hidePopup();
    }
  }

  handleKeydown(e) {
    /* ===== Handle keyboard events ===== */
    /* ===== Close on Escape ===== */
    if (e.key === 'Escape') {
      this.hidePopup();
      return;
    }

    /* ===== Trap Focus ===== */
    if (e.key === 'Tab' && this.popup) {
      const focusableElements = this.popup.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement?.focus();
        e.preventDefault();
      }
    }
  }
}

/* ===== Register Custom Element ===== */
if (!customElements.get('call-button')) {
  customElements.define('call-button', CallButton);
}

/******/ })();
